/** @jsxImportSource @emotion/react */

import {ReactNode} from "react";
import {QuoteStatuses, setQuoteNotTaken, setQuoteDeclined, unsubmitQuote, getQuote} from "api/quote";
import {useMutation, useQueryClient} from "react-query";
import LeftSidebar from "../components/LeftSidebar";
import SectionsList from "../components/SectionsList";
import {
  ErrorFilters,
  useSectionErrorContext,
} from "../contexts/sectionErrorContext";
import {useQuoteContext} from "../contexts/quoteContext";
import {useFieldsStatusContext} from "../contexts/fieldsStatusContext";
import PremiumAmountSideBar from "./PremiumAmountSideBar";
import {QUOTE_NOTES_SECTION_NAME} from "./QuoteNotesSection";
import {QUOTE_INFO_SECTION_NAME} from "./QuoteInfoSection";
import {INSURED_INFO_SECTION_NAME} from "./InsuredInfoForm";
import Box from "components/Box";
import Button from "components/Button";
import {css} from "@emotion/react";
import {getUserFriendlyErrorSummary} from "utils/errorSummary";
import {usePermissions} from "hooks/auth";
import {Permissions} from "api/auth";
import {QuoteTypes} from "api/quote";

const UNDERWRITER_SECTION_NAME = "Underwriting Questions";

interface Sections {
  [key: string]: ReactNode;
}

export interface Form {
  sections: Sections;
  [UNDERWRITER_SECTION_NAME]: ReactNode;
}

export default function RatePanelLeftBar({
  formSections,
  showNotesSection,
  fieldErrorsSummary,
  quoteType,
}: {
  formSections: Sections;
  showNotesSection: boolean;
  fieldErrorsSummary?: string;
  quoteType: QuoteTypes;
}) {
  const {status} = useQuoteContext();
  const {getSectionErrorsCount} = useSectionErrorContext();
  const {showRequiredErrors} = useFieldsStatusContext();

  return (
    <LeftSidebar>
      {fieldErrorsSummary && (
        <Box
          css={css`
            padding: 12px;
            background-color: #fee2e2;
            border-radius: 4px;
            margin-bottom: 20px;
          `}
        >
          <div
            css={css`
              font-size: 13px;
              color: #333;
            `}
          >
            {getUserFriendlyErrorSummary(fieldErrorsSummary)}
          </div>
        </Box>
      )}
      <SectionsList
        sections={[QUOTE_INFO_SECTION_NAME, INSURED_INFO_SECTION_NAME]
          .concat(Object.keys(formSections))
          .map((section) => ({
            title: section,
            id: formatSectionNameId(section),
            errors: getSectionErrorsCount(
              section,
              showRequiredErrors ? ErrorFilters.ALL : ErrorFilters.NONREQUIRED
            ),
          }))}
      />
      {showNotesSection && (
        <SectionsList
          sections={[
            {
              title: QUOTE_NOTES_SECTION_NAME,
              id: formatSectionNameId(QUOTE_NOTES_SECTION_NAME),
            },
          ]}
        />
      )}
      {quoteType !== QuoteTypes.OTHER && <PremiumAmountSideBar />}
      {status !== QuoteStatuses.QUICK_QUOTE && (
        <SectionsList
          sections={[
            {
              title: UNDERWRITER_SECTION_NAME,
              id: formatSectionNameId(UNDERWRITER_SECTION_NAME),
              errors: getSectionErrorsCount(
                UNDERWRITER_SECTION_NAME,
                showRequiredErrors ? ErrorFilters.ALL : ErrorFilters.NONREQUIRED
              ),
            },
          ]}
        />
      )}
      <ActionButtons />
    </LeftSidebar>
  );
}

function ActionButtons() {
  const {status} = useQuoteContext();
  const showMarkAsButtons =
    status !== QuoteStatuses.NOT_TAKEN &&
    status !== QuoteStatuses.DECLINED &&
    status !== QuoteStatuses.QUICK_DECLINE;
  const showUnsubmitButton =
    status === QuoteStatuses.NOT_TAKEN ||
    status === QuoteStatuses.DECLINED ||
    status === QuoteStatuses.QUICK_DECLINE;

  return (
    <Box
      css={css`
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      `}
    >
      {showMarkAsButtons && <MarkAsNotTakenButton />}
      {showMarkAsButtons && <MarkAsDeclinedButton />}
      {showUnsubmitButton && <UnsubmitQuoteButton />}
    </Box>
  );
}

function MarkAsNotTakenButton() {
  const queryClient = useQueryClient();
  const {quoteId} = useQuoteContext();
  const permissions = usePermissions();

  const {mutateAsync: mutateSetQuoteNotTaken, isLoading: settingNotTaken} =
    useMutation(() => setQuoteNotTaken(quoteId), {
      onSuccess: async () => {
        await queryClient.invalidateQueries([getQuote.name, quoteId.toString()]);
      },
    });

  if (!permissions.includes(Permissions.SET_QUOTE_NOT_TAKEN)) {
    return null;
  }

  return (
    <Button
      background
      size="large"
      color="warning"
      isLoading={settingNotTaken}
      onClick={() => mutateSetQuoteNotTaken()}
    >
      Mark as Not Taken
    </Button>
  );
}

function MarkAsDeclinedButton() {
  const queryClient = useQueryClient();
  const {quoteId} = useQuoteContext();
  const permissions = usePermissions();

  const {mutateAsync: mutateSetQuoteDeclined, isLoading: settingDeclined} =
    useMutation(() => setQuoteDeclined(quoteId), {
      onSuccess: async () => {
        await queryClient.invalidateQueries([getQuote.name, quoteId.toString()]);
      },
    });

  if (!permissions.includes(Permissions.SET_QUOTE_DECLINED)) {
    return null;
  }

  return (
    <Button
      background
      size="large"
      color="danger"
      isLoading={settingDeclined}
      onClick={() => mutateSetQuoteDeclined()}
    >
      Mark as Declined
    </Button>
  );
}

function UnsubmitQuoteButton() {
  const queryClient = useQueryClient();
  const {quoteId} = useQuoteContext();
  const permissions = usePermissions();

  const {mutateAsync: mutateUnSubmitQuote, isLoading: unsubmittingQuote} =
    useMutation(() => unsubmitQuote(quoteId), {
      onSuccess: async () => {
        await queryClient.invalidateQueries([getQuote.name, quoteId.toString()]);
      },
    });

  if (!permissions.includes(Permissions.UNSUBMIT_QUOTE_ALWAYS)) {
    return null;
  }

  return (
    <Button
      background
      size="large"
      color="primary"
      isLoading={unsubmittingQuote}
      onClick={() => mutateUnSubmitQuote()}
    >
      Unsubmit Quote
    </Button>
  );
}

function formatSectionNameId(sectionName: string) {
  return sectionName.replace(" ", "-");
}
